/** Yaqin orientatsiyalar / ishga chiqish kunlari: N kun ichida, carrier va driverning hozirgi mahalliy vaqti bilan. */
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNow } from "@/lib/useNow";
import { driverTz, tzAbbr } from "@/features/drivers/timezones";
import type { Me } from "@/features/auth/types";
import type { WidgetItem } from "../types";
import { useWidgetData } from "../data";
import { autoHour12, clockText, zoneParts } from "../clock/time";
import { BigNumber, WidgetEmpty, WidgetRow } from "../ui";
import type { StartsConfig } from "./index";

type Props = { item: WidgetItem<StartsConfig>; me: Me };
interface StartRow {
  driver_id: number;
  name: string;
  state: string;
  start_date: string;
  status: string;
  status_label: string;
  carrier: string;
}
type Data = { drivers: StartRow[]; days: number; today: string };
const ENTITIES = ["driver", "application"];

const utcOf = (key: string) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const dayText = (lang: string, key: string, today: string, t: (k: string) => string) =>
  key === today ? t("calendar.today") : new Intl.DateTimeFormat(lang, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(utcOf(key));

function Row({ r, today, now, lang, hour12 }: { r: StartRow; today: string; now: Date; lang: string; hour12: boolean }) {
  const { t } = useTranslation("widgets");
  const navigate = useNavigate();
  const tz = driverTz(r.state);
  const local = r.state ? `${r.state} ${clockText(zoneParts(now, tz), hour12).time} ${tzAbbr(tz, now)}` : "";
  return (
    <WidgetRow
      title={r.name}
      sub={[r.carrier || r.status_label, local].filter(Boolean).join(" · ")}
      right={<span className={cn("capitalize", r.start_date === today && "font-semibold text-ink")}>{dayText(lang, r.start_date, today, t)}</span>}
      onClick={() => navigate(`/drivers/${r.driver_id}`)}
    />
  );
}

export function StartsView({ item }: Props) {
  const { t, i18n } = useTranslation("widgets");
  const lang = i18n.resolvedLanguage ?? i18n.language;
  const now = useNow("minute");
  const q = useWidgetData<Data>("starts", { days: item.config.days }, ENTITIES);
  const rows = q.data?.drivers ?? [];
  const today = q.data?.today ?? "";
  const hour12 = autoHour12(lang);
  const emptyText = t(q.isError ? "failed" : "starts.empty", { days: item.config.days });
  const label = t("starts.within", { days: item.config.days });

  if (item.size === "s") {
    const first = rows[0];
    return (
      <div className="flex h-full flex-col px-3 py-2.5">
        <BigNumber value={q.isPending ? "–" : rows.length} label={label} tone={rows.length ? undefined : "text-muted"} />
        <div className="mt-auto min-w-0">
          {first ? (
            <>
              <div className="truncate text-[12px] font-medium">{first.name}</div>
              <div className="truncate text-[11px] text-muted capitalize">{dayText(lang, first.start_date, today, t)}{first.carrier ? ` · ${first.carrier}` : ""}</div>
            </>
          ) : (
            !q.isPending && <div className="text-[11px] text-muted">{emptyText}</div>
          )}
        </div>
      </div>
    );
  }
  const shown = rows.slice(0, item.size === "m" ? 3 : 8);
  return (
    <div className="flex h-full flex-col px-3 pt-2 pb-1.5">
      <div className="mb-0.5 flex items-baseline gap-1.5 px-1.5">
        <span className={cn("text-[22px] leading-none font-semibold tracking-[-0.02em] tabular-nums", rows.length === 0 && "text-muted")}>{q.isPending ? "–" : rows.length}</span>
        <span className="truncate text-[11px] text-muted">{label}</span>
      </div>
      <div className={cn("min-h-0 flex-1", item.size === "m" ? "overflow-hidden" : "overflow-y-auto")}>
        {shown.length ? shown.map((r) => <Row key={r.driver_id} r={r} today={today} now={now} lang={lang} hour12={hour12} />) : !q.isPending && <WidgetEmpty text={emptyText} />}
      </div>
    </div>
  );
}

export function StartsGlance({ item }: Props) {
  const { t } = useTranslation("widgets");
  const q = useWidgetData<Data>("starts", { days: item.config.days }, ENTITIES);
  const n = q.data?.drivers.length ?? 0;
  return (
    <span className="inline-flex items-center gap-1.5" title={t("starts.title")}>
      <Truck size={13} strokeWidth={1.75} className="text-muted" aria-hidden />
      <span className={cn("text-[12px] font-semibold tabular-nums", n === 0 && "text-muted")}>{n}</span>
    </span>
  );
}
